import { ConnectionNameInput } from "../../ConnectionNameInput/ConnectionNameInput";
import React, { useEffect, useReducer, useState } from "react";
import { ProviderDropdown } from "../../../ProviderDropdown/ProviderDropdown";
import { IOIDCIdp, OIDCIdpWithoutId } from "../../../../../domain/IdentityProvider";
import { Typography } from "@optimizely/axiom";
import { AuthorizationURLField } from "./AuthorizationURLField";
import { IssuerURLField } from "./IssuerURLField";
import { JsonWebKeySetURLField } from "./JsonWebKeySetURLField";
import { TokenURLField } from "./TokenURLField";
import { WellKnownMetadataURLField } from "./WellKnownMetadataURLField";
import { ClientIdField } from "./ClientIdField";
import { ClientSecretField } from "./ClientSecretField";
import { UserInfoURLField } from "./UserInfoURLField";

export const emptyForm = {
    authorizationUrl: "",
    idpName: "",
    clientId: "",
    clientSecret: "",
    issuerUrl: "",
    jwksUrl: "",
    organizationId: "",
    provider: "",
    tokenUrl: "",
    type: "OIDC",
    userInfoUrl: "",
    wellKnownUrl: ""
};

const fieldReducer = (state: OIDCIdpWithoutId, changes: { [key: string]: any }) => {
    return {
        ...state,
        ...changes
    };
};

type SSOOIDCFormFieldProps = {
    disabled?: boolean;
    onChange?: (values: OIDCIdpWithoutId) => void;
    onError?: (hasError: boolean) => void;
    idp?: IOIDCIdp;
};

export const SSOOIDCFormFields = ({ disabled, idp, onChange, onError }: SSOOIDCFormFieldProps) => {
    const [values, updateValues] = useReducer<
        (state: OIDCIdpWithoutId, changes: { [key: string]: any }) => OIDCIdpWithoutId
    >(fieldReducer, {
        ...emptyForm,
        ...(idp || {}),
        type: "OIDC"
    });
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [urlErrors, setUrlErrors] = useState<{ [key: string]: boolean }>({});

    const handleRequiredField = ({ name, value }: { name: string; value: any }) => {
        if (!value) {
            setErrors({
                ...errors,
                [name]: true
            });
        } else {
            const updatedErrors = { ...errors };
            delete updatedErrors[name];
            setErrors(updatedErrors);
        }
        updateValues({ [name]: value });
    };

    const handleUrlField = (name: string, value: string, hasError?: boolean) => {
        setUrlErrors((prev) => {
            if (hasError) {
                return { ...prev, [name]: true };
            }
            const next = { ...prev };
            delete next[name];
            return next;
        });
        updateValues({ [name]: value });
    };

    const hasManualValues = ["authorizationUrl", "issuerUrl", "jwksUrl", "tokenUrl", "userInfoUrl"].find(
        (val) => !!values[val as keyof OIDCIdpWithoutId]
    );

    useEffect(() => {
        onChange && onChange(values);
    }, [onChange, values]);

    useEffect(() => {
        onError && onError(Object.values(urlErrors).some(Boolean));
    }, [onError, urlErrors]);

    return (
        <>
            <ConnectionNameInput
                onChange={(value) => handleRequiredField({ name: "idpName", value })}
                disabled={disabled}
                value={values.idpName}
            />
            <ProviderDropdown
                onChange={(value: string) => updateValues({ provider: value })}
                value={values.provider}
                disabled={disabled}
            />
            {!disabled && (
                <>
                    <ClientIdField onChange={(value) => handleRequiredField({ name: "clientId", value })} required />
                    <ClientSecretField
                        onChange={(value) => handleRequiredField({ name: "clientSecret", value })}
                        required
                    />
                </>
            )}
            {!disabled && (
                <Typography type="subhead" tag="div" className="push-triple--top">
                    AND
                </Typography>
            )}
            {(!disabled || idp?.wellKnownUrl) && (
                <WellKnownMetadataURLField
                    value={values.wellKnownUrl}
                    onChange={(value, hasError) => handleUrlField("wellKnownUrl", value, hasError)}
                    disabled={!!hasManualValues || disabled}
                />
            )}
            {!disabled && (
                <>
                    <Typography type="subhead" tag="div" className="push-triple--top">
                        OR
                    </Typography>
                </>
            )}
            <AuthorizationURLField
                value={values.authorizationUrl}
                onChange={(value, hasError) => handleUrlField("authorizationUrl", value, hasError)}
                disabled={!!values.wellKnownUrl || !!idp?.authorizationUrl || disabled}
            />
            <IssuerURLField
                value={values.issuerUrl || ""}
                onChange={(value) => updateValues({ issuerUrl: value })}
                disabled={!!values.wellKnownUrl || !!idp?.issuerUrl || disabled}
                hideHelperText={!!values.wellKnownUrl || !!idp?.issuerUrl}
            />
            <JsonWebKeySetURLField
                value={values.jwksUrl}
                onChange={(value, hasError) => handleUrlField("jwksUrl", value, hasError)}
                disabled={!!values.wellKnownUrl || !!idp?.jwksUrl || disabled}
            />
            <TokenURLField
                value={values.tokenUrl}
                onChange={(value, hasError) => handleUrlField("tokenUrl", value, hasError)}
                disabled={!!values.wellKnownUrl || !!idp?.tokenUrl || disabled}
            />
            <UserInfoURLField
                onChange={(value, hasError) => handleUrlField("userInfoUrl", value, hasError)}
                disabled={!!values.wellKnownUrl || !!idp?.userInfoUrl || disabled}
                value={values.userInfoUrl}
            />
        </>
    );
};

SSOOIDCFormFields.displayName = "SSOOIDCFormFields";
